import React, { FormEvent, MouseEventHandler } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';

// types
import { Search } from 'js/auth/types/search';
import { onChangeTypeDef } from 'js/auth/types/formInput';

// styles
import { mq } from 'js/common/utils/mediaQueries';
import { VercelFormInput, VercelGoogleAuthButton, VercelRedirectText, vercelTextLinkStyle } from './styles/vercel';
import { VercelLineDivider } from 'js/common/components/Vercel/styles/common';

// components
import VercelPage from 'js/common/components/Vercel/VercelPage';
import VercelLayout from 'js/common/components/Vercel/VercelLayout';
import AuthErrorBanner from './AuthErrorBanner';

const WideAuthErrorBanner = styled(AuthErrorBanner)`
  box-sizing: border-box;
  width: 100%;
  margin-bottom: 20px;
`;

const ChangeEmailAnchor = styled.a`
  ${vercelTextLinkStyle}
  position: absolute;
  right: ${(props: { hasError: boolean }) => (props.hasError ? '33px' : '12px')};
  ${mq({
    top: ['40px', '40px', '33px', '33px'],
  })}
`;

const EmailInputContainer = styled.div`
  position: relative;
`;

interface Props {
  search: Search;
  email: string;
  password: string;
  emailTooltipText: string;
  authErrorCode: string;
  marketingUrl?: string;
  siteName: string;
  showInputErrors: boolean;
  showPasswordInput: boolean;
  disableEmailInput: boolean;
  isSubmitDisabled: boolean;
  isGoogleAuthEnabled: boolean;
  isLoginDisabled: boolean;
  onEmailChange: onChangeTypeDef;
  onPasswordChange: onChangeTypeDef;
  onChangeEmailClick: MouseEventHandler<HTMLAnchorElement>;
  clientState: Search;
  onSubmit(e: FormEvent<HTMLFormElement>): Promise<void>;
}

export default function VercelLoginForm({
  search,
  email,
  password,
  emailTooltipText,
  authErrorCode,
  marketingUrl,
  siteName,
  showInputErrors,
  showPasswordInput,
  disableEmailInput,
  isSubmitDisabled,
  onEmailChange,
  onPasswordChange,
  onChangeEmailClick,
  isGoogleAuthEnabled,
  isLoginDisabled,
  clientState,
  onSubmit,
}: Props) {
  return (
    <VercelPage pageTitle="Log in">
      <VercelLayout
        isForm
        formProps={{ onSubmit, disabled: isSubmitDisabled, buttonText: 'Continue' }}
        title="Sign Into MongoDB"
      >
        {isGoogleAuthEnabled && (
          <>
            <VercelGoogleAuthButton
              clientState={clientState}
              disabled={isLoginDisabled}
              displayText="Sign in with Google"
              location={location}
            />
            <VercelLineDivider>or</VercelLineDivider>
          </>
        )}

        {authErrorCode && (
          <WideAuthErrorBanner authErrorCode={authErrorCode} marketingUrl={marketingUrl} siteName={siteName} />
        )}
        <EmailInputContainer>
          <VercelFormInput
            fieldName="username"
            labelName="Email Address"
            autoComplete="email"
            onChange={onEmailChange}
            value={email}
            hasError={showInputErrors}
            tooltipText={emailTooltipText}
            disabled={disableEmailInput}
            showChangeUsername={showPasswordInput}
            autoFocus
          />
          {disableEmailInput && (
            <ChangeEmailAnchor tabIndex={-1} role="button" onClick={onChangeEmailClick} hasError={showInputErrors}>
              Change
            </ChangeEmailAnchor>
          )}
        </EmailInputContainer>
        {showPasswordInput && (
          <>
            <VercelFormInput
              fieldName="password"
              labelName="Password"
              autoComplete="current-password"
              type="password"
              hasError={showInputErrors}
              onChange={onPasswordChange}
              value={password}
              autoFocus
            />
          </>
        )}
        <VercelRedirectText>
          {showPasswordInput ? (
            <Link
              to={{
                pathname: '/reset/password',
                search: `?email=${encodeURIComponent(email)}`,
              }}
              css={vercelTextLinkStyle}
            >
              Forgot Password?
            </Link>
          ) : (
            <>
              Don&apos;t have a MongoDB account?
              <Link to={`/register/vercel${!!search ? search + '&' : '?'}signedOut=true`}>&nbsp;Sign up now</Link>
            </>
          )}
        </VercelRedirectText>
      </VercelLayout>
    </VercelPage>
  );
}
